import {EvidenceRef, type DocumentArtifact, type InsightEvidence, type IssueEvidence, type Signal, type Ticket} from "@devplaninc/codeup";
import type {HydrateEvidenceRequest} from "@devplaninc/codeup";

export function emptyEvidenceHydrationRefs(): HydrateEvidenceRequest {
  return {
    workspaceId: "",
    refs: [],
  };
}

export function collectEvidenceHydrationRefs(evidence: InsightEvidence[]): HydrateEvidenceRequest {
  const refs = emptyEvidenceHydrationRefs();
  const seen = new Set<string>();

  for (const item of evidence) {
    if (!item.ref) continue;
    const key = evidenceRequestKey(item.ref);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    refs.refs.push(item.ref);
  }

  return refs;
}

export function collectSignalCallNoteEvidence(signals: Signal[]): InsightEvidence[] {
  const evidence: InsightEvidence[] = [];
  const seen = new Set<string>();

  for (const signal of signals) {
    for (const artifact of signal.artifacts) {
      const details = artifact.details;
      if (details?.$case !== "callNote") continue;
      const key = callNoteProviderKey(details.value.providerType, details.value.noteId);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      evidence.push({
        ref: {
          target: {
            $case: "callNote",
            value: {
              providerType: details.value.providerType,
              noteId: details.value.noteId,
            },
          },
        },
        note: "",
        createdAt: undefined,
      });
    }
  }

  return evidence;
}

export function collectSignalDocumentEvidence(signals: Signal[]): InsightEvidence[] {
  const evidence: InsightEvidence[] = [];
  const seen = new Set<string>();

  for (const signal of signals) {
    for (const artifact of signal.artifacts) {
      const details = artifact.details;
      if (details?.$case !== "document") continue;
      const key = documentEvidenceKey(details.value);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      evidence.push({
        ref: {
          target: {
            $case: "document",
            value: {
              provider: details.value.provider,
              documentId: details.value.documentId,
            },
          },
        },
        note: "",
        createdAt: undefined,
      });
    }
  }

  return evidence;
}

export function collectSignalIssueEvidence(signals: Signal[]): InsightEvidence[] {
  const evidence: InsightEvidence[] = [];
  const seen = new Set<string>();

  for (const signal of signals) {
    for (const artifact of signal.artifacts) {
      const details = artifact.details;
      if (details?.$case !== "issue") continue;
      const issue = {
        tracker: details.value.tracker,
        project: "",
        id: details.value.issueId,
        title: details.value.title,
      };
      const key = issueEvidenceKey(issue);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      evidence.push({
        ref: {target: {$case: "issue", value: issue}},
        note: "",
        createdAt: undefined,
      });
    }
  }

  return evidence;
}

export function evidenceHydrationKeys(refs: HydrateEvidenceRequest): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ref of refs.refs) {
    const key = evidenceHydrationKey(ref);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export function hasEvidenceHydrationRefs(refs: HydrateEvidenceRequest): boolean {
  return refs.refs.length > 0;
}

export function issueEvidenceKey(issue: IssueEvidence): string {
  return entityKey("issue", `${issue.tracker}:${issueIdentifier(issue.project, issue.id)}`);
}

export function ticketKey(ticket: Ticket): string {
  const summary = ticket.summary;
  if (!summary?.tracker || !summary.id) return "";
  return entityKey("issue", `${summary.tracker}:${summary.id}`);
}

export function documentEvidenceKey(document: DocumentArtifact): string {
  const provider = document.provider.trim();
  const documentId = document.documentId.trim();
  return provider && documentId ? entityKey("document", `${provider}:${documentId}`) : "";
}

export function callNoteProviderKey(providerType: string, noteId: string): string {
  return scopedEntityKey("callNote", providerType, noteId);
}

export function evidenceRequestKey(ref: EvidenceRef): string {
  return ref.target ? JSON.stringify(EvidenceRef.toJSON(ref)) : "";
}

function evidenceHydrationKey(ref: EvidenceRef): string {
  const target = ref.target;
  if (!target) return "";

  switch (target.$case) {
    case "signalId":
      return entityKey("signal", target.value);
    case "changeId":
      return entityKey("change", target.value);
    case "discussionTopicId":
      return entityKey("discussionTopic", target.value);
    case "callNoteId":
      return entityKey("callNoteId", target.value);
    case "featureId":
      return entityKey("feature", target.value);
    case "pullRequest":
      return target.value.repo.trim() && target.value.number
        ? entityKey("pullRequest", `${target.value.repo.trim()}:${target.value.number}`)
        : "";
    case "gitCommit":
      return target.value.repo.trim() && target.value.sha.trim()
        ? entityKey("gitCommit", `${target.value.repo.trim()}:${target.value.sha.trim()}`)
        : "";
    case "issue":
      return issueEvidenceKey(target.value);
    case "document":
      return documentEvidenceKey(target.value);
    case "callNote":
      return callNoteProviderKey(target.value.providerType, target.value.noteId);
    case "discussionTopic":
      return entityKey("discussionTopic", target.value.topicId);
    case "insightId":
      return entityKey("insight", target.value);
    case "text":
      return entityKey("text", target.value);
  }
}

function entityKey(kind: string, id: string): string {
  const trimmed = id.trim();
  return trimmed ? `${kind}:${trimmed}` : "";
}

function scopedEntityKey(kind: string, scope: string, id: string): string {
  const trimmedScope = scope.trim();
  const trimmedId = id.trim();
  return trimmedScope && trimmedId ? `${kind}:${trimmedScope}:${trimmedId}` : "";
}

function issueIdentifier(project: string, id: string): string {
  const trimmedId = id.trim();
  const trimmedProject = project.trim();
  if (!trimmedProject || trimmedId.toUpperCase().startsWith(`${trimmedProject.toUpperCase()}-`)) return trimmedId;
  return `${trimmedProject}-${trimmedId}`;
}
